import React from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';
import SEO from '@/components/SEO';

const PrivacyPolicy = () => {
  const { language } = useLanguage();

  const content = {
    fr: {
      title: 'Politique de confidentialité',
      lastUpdated: 'Dernière mise à jour : Juillet 2025',
      sections: [
        {
          title: '1. Introduction',
          content: `Byrōn Properties SA ("nous", "notre", "nos") s'engage à protéger la confidentialité de vos données personnelles. Cette politique de confidentialité explique comment nous collectons, utilisons et protégeons vos informations conformément à la Loi fédérale suisse sur la protection des données (LPD) et, le cas échéant, au Règlement général sur la protection des données (RGPD) de l'Union européenne.`
        },
        {
          title: '2. Responsable du traitement',
          content: `Byrōn Properties SA
Chemin de Byron 12
1228 Plan-les-Ouates
Genève, Suisse
Email : privacy@byron-properties.ch
Téléphone : +41 22 700 12 34`
        },
        {
          title: '3. Données collectées',
          content: `Nous collectons les catégories de données suivantes :

• Données d'identification : nom, prénom, adresse email, numéro de téléphone
• Données de recherche immobilière : budget, localisation souhaitée, type de bien, critères de recherche
• Données de communication : messages, correspondances, demandes de contact
• Données techniques : adresse IP, type de navigateur, pages visitées (via cookies)
• Données d'estimation : adresse du bien, caractéristiques, photos (si fournies)`
        },
        {
          title: '4. Finalités du traitement',
          content: `Vos données sont traitées pour :

• Répondre à vos demandes de contact et d'information
• Vous présenter des biens immobiliers correspondant à vos critères
• Traiter vos demandes d'estimation
• Gérer votre accès à notre collection off-market
• Améliorer nos services et notre site web
• Respecter nos obligations légales`
        },
        {
          title: '5. Base juridique',
          content: `Le traitement de vos données repose sur :

• Votre consentement (formulaires de contact, newsletter)
• L'exécution d'un contrat ou de mesures précontractuelles
• Nos intérêts légitimes (amélioration des services, sécurité)
• Le respect d'obligations légales`
        },
        {
          title: '6. Partage des données',
          content: `Vos données peuvent être partagées avec :

• Nos partenaires immobiliers et notaires (uniquement avec votre accord)
• Nos prestataires techniques (hébergement, email)
• Les autorités compétentes (si requis par la loi)

Nous ne vendons jamais vos données personnelles à des tiers.`
        },
        {
          title: '7. Transferts internationaux',
          content: `Vos données sont principalement stockées en Suisse. En cas de transfert vers des pays hors de la Suisse ou de l'EEE, nous assurons un niveau de protection adéquat par des clauses contractuelles types ou d'autres mécanismes approuvés.`
        },
        {
          title: '8. Conservation des données',
          content: `Nous conservons vos données :

• Demandes de contact : 2 ans après le dernier contact
• Données de transaction : 10 ans (obligations légales suisses)
• Données de navigation : 13 mois maximum
• Compte client : durée de la relation + 2 ans`
        },
        {
          title: '9. Vos droits',
          content: `Conformément à la LPD et au RGPD, vous disposez des droits suivants :

• Droit d'accès à vos données
• Droit de rectification
• Droit à l'effacement ("droit à l'oubli")
• Droit à la limitation du traitement
• Droit à la portabilité des données
• Droit d'opposition
• Droit de retirer votre consentement

Pour exercer ces droits, contactez-nous à privacy@byron-properties.ch`
        },
        {
          title: '10. Sécurité',
          content: `Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données contre tout accès non autorisé, modification, divulgation ou destruction. Cela inclut le chiffrement SSL/TLS, l'authentification sécurisée et la limitation des accès.`
        },
        {
          title: '11. Modifications',
          content: `Nous pouvons modifier cette politique de confidentialité à tout moment. Les modifications seront publiées sur cette page avec une date de mise à jour. Nous vous encourageons à consulter régulièrement cette page.`
        },
        {
          title: '12. Contact',
          content: `Pour toute question concernant cette politique ou vos données personnelles :

Byrōn Properties SA
Délégué à la protection des données
Chemin de Byron 12
1228 Plan-les-Ouates, Genève
Email : privacy@byron-properties.ch

Vous avez également le droit de déposer une plainte auprès du Préposé fédéral à la protection des données et à la transparence (PFPDT).`
        }
      ]
    },
    en: {
      title: 'Privacy Policy',
      lastUpdated: 'Last updated: July 2025',
      sections: [
        {
          title: '1. Introduction',
          content: `Byrōn Properties SA ("we", "our", "us") is committed to protecting the privacy of your personal data. This privacy policy explains how we collect, use and protect your information in accordance with the Swiss Federal Act on Data Protection (FADP) and, where applicable, the European Union General Data Protection Regulation (GDPR).`
        },
        {
          title: '2. Data Controller',
          content: `Byrōn Properties SA
Chemin de Byron 12
1228 Plan-les-Ouates
Geneva, Switzerland
Email: privacy@byron-properties.ch
Phone: +41 22 700 12 34`
        },
        {
          title: '3. Data Collected',
          content: `We collect the following categories of data:

• Identification data: name, surname, email address, phone number
• Property search data: budget, desired location, property type, search criteria
• Communication data: messages, correspondence, contact requests
• Technical data: IP address, browser type, pages visited (via cookies)
• Valuation data: property address, features, photos (if provided)`
        },
        {
          title: '4. Purpose of Processing',
          content: `Your data is processed to:

• Respond to your contact and information requests
• Present you with properties matching your criteria
• Process your valuation requests
• Manage your access to our off-market collection
• Improve our services and website
• Comply with our legal obligations`
        },
        {
          title: '5. Legal Basis',
          content: `The processing of your data is based on:

• Your consent (contact forms, newsletter)
• Performance of a contract or pre-contractual measures
• Our legitimate interests (service improvement, security)
• Compliance with legal obligations`
        },
        {
          title: '6. Data Sharing',
          content: `Your data may be shared with:

• Our real estate partners and notaries (only with your agreement)
• Our technical service providers (hosting, email)
• Competent authorities (if required by law)

We never sell your personal data to third parties.`
        },
        {
          title: '7. International Transfers',
          content: `Your data is primarily stored in Switzerland. In case of transfer to countries outside Switzerland or the EEA, we ensure an adequate level of protection through standard contractual clauses or other approved mechanisms.`
        },
        {
          title: '8. Data Retention',
          content: `We retain your data:

• Contact requests: 2 years after last contact
• Transaction data: 10 years (Swiss legal requirements)
• Browsing data: maximum 13 months
• Customer account: duration of relationship + 2 years`
        },
        {
          title: '9. Your Rights',
          content: `Under the FADP and GDPR, you have the following rights:

• Right of access to your data
• Right to rectification
• Right to erasure ("right to be forgotten")
• Right to restriction of processing
• Right to data portability
• Right to object
• Right to withdraw consent

To exercise these rights, contact us at privacy@byron-properties.ch`
        },
        {
          title: '10. Security',
          content: `We implement appropriate technical and organizational measures to protect your data against unauthorized access, modification, disclosure or destruction. This includes SSL/TLS encryption, secure authentication and access limitations.`
        },
        {
          title: '11. Changes',
          content: `We may modify this privacy policy at any time. Changes will be posted on this page with an update date. We encourage you to check this page regularly.`
        },
        {
          title: '12. Contact',
          content: `For any questions regarding this policy or your personal data:

Byrōn Properties SA
Data Protection Officer
Chemin de Byron 12
1228 Plan-les-Ouates, Geneva
Email: privacy@byron-properties.ch

You also have the right to file a complaint with the Federal Data Protection and Information Commissioner (FDPIC).`
        }
      ]
    }
  };

  const t = content[language] || content.fr;

  return (
    <main className="min-h-screen bg-byron-bg" data-testid="privacy-policy-page">
      <SEO 
        title={language === 'fr' ? 'Politique de confidentialité' : 'Privacy Policy'}
        description={language === 'fr' 
          ? 'Politique de confidentialité de Byrōn Properties. Comment nous collectons, utilisons et protégeons vos données personnelles.'
          : 'Byrōn Properties privacy policy. How we collect, use and protect your personal data.'}
        canonical="/privacy"
      />

      {/* Header spacer */}
      <div className="header-spacer" />

      {/* Content */}
      <section className="py-10 md:py-14">
        <div className="container-byron">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h1 className="heading-primary text-center mb-4">{t.title}</h1>
            <p className="text-sm text-[#9B9B9B] text-center mb-12">{t.lastUpdated}</p>

            <div className="space-y-10">
              {t.sections.map((section, index) => (
                <div key={index}>
                  <h2 className="text-lg font-medium text-[#1A1A1A] mb-4">{section.title}</h2>
                  <div className="text-[#6B6B6B] text-sm leading-relaxed whitespace-pre-line">
                    {section.content}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default PrivacyPolicy;
